// Port of backend/src/models/Quiz.js — kept in sync so the landing-page intake
// can preview the user's eligibility WITHOUT a backend call. The portal
// re-computes server-side after signup; this is the source of truth there.
// If you change rules here, mirror them in Quiz.statics.computeEligibility.
//
// Result categories
// ─────────────────
// Internally the legacy `status` is kept (ELIGIBLE / CONDITIONAL / NOT_ELIGIBLE)
// so the portal/backend doesn't break. Externally we also return `category`
// — the four spec-compliant buckets from Website Build Handoff V1.0 sec. 5:
//   • urgent           — red-flag symptoms; route to emergency care, skip funnel
//   • not_suitable     — absolute contraindication or out-of-range BMI / EDO
//   • doctor_review    — high-risk condition, upcoming surgery, borderline BMI
//   • may_be_suitable  — eligible for doctor review and possible treatment
//
// Public-facing copy MUST NEVER say "approved for medication" or imply a
// prescription. Use phrases like "may be suitable for doctor review".

export function computeBmi(heightCm, weightKg) {
  const h = Number(heightCm);
  const w = Number(weightKg);
  if (!h || !w) return null;
  const m = h / 100;
  if (m <= 0) return null;
  return Math.round((w / (m * m)) * 10) / 10;
}

// Spec sec. 5 — Red-Flag Symptoms screen. If ANY is true, the user should be
// routed to urgent medical attention and the assessment should not produce a
// "may be suitable" result.
export function hasUrgentSymptoms(urgent = {}) {
  return !!(
    urgent.severeAbdominalPain ||
    urgent.repeatedVomiting ||
    urgent.allergicReaction ||
    urgent.breathingDifficulty ||
    urgent.chestPain ||
    urgent.faintingOrConfusion ||
    urgent.severeDehydration
  );
}

export function computeEligibility(quiz) {
  // ── 1. Urgent symptoms override everything ───────────────────────────────
  if (hasUrgentSymptoms(quiz.urgentSymptoms)) {
    return {
      status: 'NOT_ELIGIBLE',
      category: 'urgent',
      reason:
        'Some of the symptoms you described need immediate medical care. ' +
        'Please contact a healthcare provider or visit a hospital now — do not wait.',
    };
  }

  // ── 2. Absolute contraindications → not suitable online ──────────────────
  const c = quiz.contraindications || {};
  if (c.mtcHistory || c.menType2 || c.glp1Allergy || c.pregnantOrTrying) {
    return {
      status: 'NOT_ELIGIBLE',
      category: 'not_suitable',
      reason:
        'Based on your answers, this programme may not be suitable online. ' +
        'Please speak directly with a licensed healthcare provider.',
    };
  }

  // ── 3. Eating-disorder history → not suitable online ─────────────────────
  if (quiz.lifestyle?.eatingDisorderHistory) {
    return {
      status: 'NOT_ELIGIBLE',
      category: 'not_suitable',
      reason:
        'A history of disordered eating means medical weight loss is not ' +
        'something we should start through an online assessment. Please ' +
        'speak to a healthcare provider for the right support.',
    };
  }

  // ── 4. BMI well below screening threshold → not suitable ────────────────
  const bmi = quiz.basicInfo?.bmi;
  if (typeof bmi === 'number' && bmi > 0 && bmi < 23) {
    return {
      status: 'NOT_ELIGIBLE',
      category: 'not_suitable',
      reason:
        'Your BMI is below the range where medical weight management is ' +
        'usually appropriate. Please discuss your goals with a healthcare ' +
        'provider before considering any treatment.',
    };
  }

  // ── 5. Upcoming surgery / anaesthesia → doctor review ───────────────────
  if (quiz.surgery?.upcomingSurgery || quiz.surgery?.deepSedation) {
    return {
      status: 'CONDITIONAL',
      category: 'doctor_review',
      reason:
        'You mentioned an upcoming procedure involving anaesthesia or deep ' +
        'sedation. Speak to your doctor first — GLP-1 medications can affect ' +
        'how the stomach empties before such procedures.',
    };
  }

  // ── 6. High-risk medical history → doctor review ────────────────────────
  const h = quiz.highRiskFlags || {};
  const anyHighRisk = h.pancreatitisHistory || h.gallstones || h.severeGastric ||
                      h.kidneyProblems || h.chronicVomiting;
  if (anyHighRisk) {
    return {
      status: 'CONDITIONAL',
      category: 'doctor_review',
      reason:
        'Your health profile needs a closer look by a licensed doctor before ' +
        'any treatment decision. A consultation is the right next step.',
    };
  }

  // ── 7. BMI ≥ 27 → may be suitable for doctor review ─────────────────────
  if (typeof bmi === 'number' && bmi >= 27) {
    return {
      status: 'ELIGIBLE',
      category: 'may_be_suitable',
      reason: '',
    };
  }

  // ── 8. BMI 25–27 with metabolic comorbidity → may be suitable ───────────
  const meds = quiz.currentMedications || {};
  if (typeof bmi === 'number' && bmi >= 25 && (meds.diabetesMedication || meds.oralGlucoseMedication)) {
    return {
      status: 'ELIGIBLE',
      category: 'may_be_suitable',
      reason: '',
    };
  }

  // ── 9. Otherwise borderline → doctor review ─────────────────────────────
  return {
    status: 'CONDITIONAL',
    category: 'doctor_review',
    reason:
      'Your profile is borderline. A licensed doctor will help decide whether ' +
      'medical weight management is appropriate for you.',
  };
}

// Maps target loss in kg to bottle count per PRD §5.
export function recommendBottles(targetLossKg) {
  const t = Number(targetLossKg) || 0;
  if (t <= 0) return { bottles: null, weeks: null, expectedLoss: null };
  if (t <= 4)  return { bottles: 1, weeks: 4,  expectedLoss: '2 – 4 kg' };
  if (t <= 8)  return { bottles: 2, weeks: 8,  expectedLoss: '5 – 8 kg' };
  return { bottles: 3, weeks: 12, expectedLoss: '9 – 13 kg' };
}

// Shape submitted to the portal's /quiz endpoint after signup.
export function toQuizPayload(state) {
  return {
    basicInfo: {
      name: state.basic?.name?.trim() || undefined,
      dob: state.basic?.dob || undefined,
      age: state.basic?.age ? Number(state.basic.age) : undefined,
      heightCm: state.basic?.heightCm ? Number(state.basic.heightCm) : undefined,
      weightKg: state.basic?.weightKg ? Number(state.basic.weightKg) : undefined,
      contactPhone: state.basic?.contactPhone?.trim() || undefined,
    },
    contraindications: state.contraindications || {},
    highRiskFlags: state.highRiskFlags || {},
    surgery: state.surgery || {},
    urgentSymptoms: state.urgentSymptoms || {},
    currentMedications: {
      diabetesMedication: !!state.medications?.diabetesMedication,
      oralGlucoseMedication: !!state.medications?.oralGlucoseMedication,
      weightLossSupplements: !!state.medications?.weightLossSupplements,
    },
    lifestyle: {
      goal: state.lifestyle?.goal,
      challenges: state.lifestyle?.challenges || [],
      targetLossKg: state.lifestyle?.targetLossKg ? Number(state.lifestyle.targetLossKg) : undefined,
      eatingDisorderHistory: !!state.lifestyle?.eatingDisorderHistory,
    },
    consent: state.consent || {},
  };
}
